import {useState} from 'react';
import classNames from 'classnames';
import {ChevronLeftIcon, ChevronRightIcon} from 'lucide-react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';
import {PagedResult} from '../../api/types.ts';
import Icon from './Icon.tsx';

interface PagerProps {
    pagedResult: PagedResult<unknown>;
    onPageSelected: (page: number) => void;
    onPerPageSelected?: (perPage: number) => void;
    // What the rows are, for "1–50 of 412 users".
    noun?: string;
}

/*
 * Numbered pages, for tables people refer to by page. Previous and Next are
 * words; the current page gets a purple outline. Galleries use "Load more"
 * instead.
 */
const Pager: React.FC<PagerProps> = ({
    pagedResult,
    onPageSelected,
    onPerPageSelected,
    noun
}) => {
    const {count, perPage, page, total} = pagedResult;
    const totalPages = Math.max(1, Math.ceil(count / perPage));
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const go = (next: number) => {
        if (next >= 1 && next <= totalPages) {
            onPageSelected(next);
        }
    };

    let startPage = Math.max(page - 2, 1);
    const endPage = Math.min(startPage + 4, totalPages);
    if (endPage - startPage < 4) {
        startPage = Math.max(endPage - 4, 1);
    }
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    const first = count === 0 ? 0 : (page - 1) * perPage + 1;
    const last = Math.min(page * perPage, count);
    const shown = total || count;

    return (
        <nav aria-label="Pagination" className="mx-pager">
            <span className="mx-pager-summary">
                {first}–{last} of {shown}
                {noun ? ` ${noun}` : ''}
            </span>
            <div className="mx-pager-pages">
                <button
                    type="button"
                    className="btn btn-tertiary btn-sm"
                    disabled={page === 1}
                    onClick={() => go(page - 1)}
                >
                    <Icon icon={ChevronLeftIcon} size="xs" strokeWidth={2} />
                    Previous
                </button>
                {startPage > 1 && <span className="mx-pager-gap">…</span>}
                {pageNumbers.map(num => (
                    <button
                        key={num}
                        type="button"
                        className={classNames('mx-pager-page', {active: num === page})}
                        aria-current={num === page ? 'page' : undefined}
                        onClick={() => go(num)}
                    >
                        {num}
                    </button>
                ))}
                {endPage < totalPages && <span className="mx-pager-gap">…</span>}
                <button
                    type="button"
                    className="btn btn-tertiary btn-sm"
                    disabled={page === totalPages}
                    onClick={() => go(page + 1)}
                >
                    Next
                    <Icon icon={ChevronRightIcon} size="xs" strokeWidth={2} />
                </button>
            </div>
            {onPerPageSelected && (
                <Dropdown
                    isOpen={dropdownOpen}
                    toggle={() => setDropdownOpen(o => !o)}
                >
                    <DropdownToggle
                        tag="button"
                        type="button"
                        className="btn btn-secondary btn-sm"
                    >
                        {perPage} per page
                    </DropdownToggle>
                    <DropdownMenu end className="mx-menu">
                        {[10, 20, 50, 100].map(option => (
                            <DropdownItem
                                key={option}
                                onClick={() => {
                                    onPerPageSelected(option);
                                    onPageSelected(1);
                                }}
                            >
                                {option} per page
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            )}
        </nav>
    );
};

export default Pager;
